import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { StarAuthShell } from "@/pages/star/StarLayout";

export default function StudentResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      toast.success(data.message || "Password reset. Please sign in.");
      navigate("/student/login", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Reset failed. The link may be invalid or expired.");
    } finally { setLoading(false); }
  };

  if (!token) {
    return (
      <StarAuthShell title="Reset password" subtitle="Invalid link">
        <Seo title="Reset Password" />
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="student-reset-notoken">
          This reset link is missing or invalid. Please request a new one.
        </div>
        <Link to="/student/forgot-password" className="mt-4 block text-center font-semibold text-blue-900 hover:underline">Request a new link</Link>
      </StarAuthShell>
    );
  }

  return (
    <StarAuthShell title="Choose a new password" subtitle="Enter and confirm your new password.">
      <Seo title="Reset Password" />
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="student-reset-form">
        <div>
          <Label className="mb-1.5 block font-semibold text-slate-700">New password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" data-testid="student-reset-password" required />
        </div>
        <div>
          <Label className="mb-1.5 block font-semibold text-slate-700">Confirm new password</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" data-testid="student-reset-confirm" required />
        </div>
        <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-900 hover:bg-blue-950" data-testid="student-reset-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set new password"}
        </Button>
        <Link to="/student/login" className="block text-center text-sm font-semibold text-blue-900 hover:underline">Back to sign in</Link>
      </form>
    </StarAuthShell>
  );
}
